import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, Calendar, User, ArrowRight, 
  Search, BookOpen, Clock, Tag, Sparkles, TrendingUp, Zap, Lightbulb
} from 'lucide-react';
import BlogImg from '../assets/Blog.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

// Mock Categories
const CATEGORIES = ['All', 'EdTech', 'Company News', 'LMS Solutions', 'Industry Trends'];

const HERO_FEATURES = [
  { Icon: Zap, top: 'Latest', bottom: 'Updates' },
  { Icon: TrendingUp, top: 'Industry', bottom: 'Trends' },
  { Icon: Lightbulb, top: 'Expert', bottom: 'Insights' },
  { Icon: Sparkles, top: 'Pro', bottom: 'Tips' },
];

// Mock Blog Data
const MOCK_POSTS = [
  {
    id: 1,
    title: 'The Future of AI in Modern EdTech Platforms',
    excerpt: 'Discover how artificial intelligence is reshaping personalized learning, automating administrative tasks, and changing the landscape of online education.',
    category: 'EdTech',
    author: 'Admin',
    date: 'Sep 24, 2026',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=800&auto=format&fit=crop',
  },
  {
    id: 2,
    title: 'SmartEdz V2.0: What to Expect in the New Release',
    excerpt: 'We are thrilled to announce the upcoming release of SmartEdz V2.0. Here is a sneak peek at the new features designed to boost institutional productivity.',
    category: 'Company News',
    author: 'Product Team',
    date: 'Sep 18, 2026',
    readTime: '4 min read',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800&auto=format&fit=crop',
  },
  {
    id: 3,
    title: '5 Ways to Increase Student Engagement in LMS',
    excerpt: 'Struggling with low course completion rates? Learn these five proven strategies to keep students engaged using gamification and interactive modules.',
    category: 'LMS Solutions',
    author: 'Sarah Jenkins',
    date: 'Sep 12, 2026',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=800&auto=format&fit=crop',
  },
  {
    id: 4,
    title: 'Bridging the Digital Divide in Rural Education',
    excerpt: 'How mobile-first learning solutions and offline-sync capabilities are helping educational institutions reach students in remote areas.',
    category: 'Industry Trends',
    author: 'Dr. Ramesh Kumar',
    date: 'Sep 05, 2026',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?q=80&w=800&auto=format&fit=crop',
  },
  {
    id: 5,
    title: 'Cloud Security in Education: Protecting Student Data',
    excerpt: 'A comprehensive guide to understanding data privacy laws and how SmartEdz ensures 100% compliance with global security standards.',
    category: 'EdTech',
    author: 'Tech Support',
    date: 'Aug 29, 2026',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=800&auto=format&fit=crop',
  },
  {
    id: 6,
    title: 'Case Study: How Global School Scaled with SmartEdz',
    excerpt: 'Read how a fast-growing international school unified their administration, grading, and parent communication using our centralized platform.',
    category: 'Company News',
    author: 'Admin',
    date: 'Aug 21, 2026',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=800&auto=format&fit=crop',
  }
];

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter posts based on category and search text
  const filteredPosts = MOCK_POSTS.filter(post => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @media (prefers-reduced-motion: reduce) { .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className={`${CONTAINER} relative py-10 lg:py-12`}>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            
            {/* Left side text and features */}
            <div className="se-fade-up">
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                <Link to="/" className="hover:text-primary transition">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Blog</span>
              </nav>
              
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">
                Insights & Updates
              </span>
              
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>
                The SmartEdz <span className="text-primary">Blog</span>
              </h1>
              
              <p className="mt-5 text-lg text-slate-500 max-w-lg leading-relaxed">
                Discover the latest insights, product updates, and expert tips on how technology is transforming the educational landscape.
              </p>

              {/* Search Bar */}
              <div className="relative max-w-lg mt-7">
                <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input 
                  type="text" 
                  placeholder="Search articles, news, or topics..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-xl ring-1 ring-slate-200 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-primary shadow-sm transition"
                />
              </div>

              {/* Icon Features */}
              <div className="grid grid-cols-4 gap-3 mt-8 max-w-lg">
                {HERO_FEATURES.map((f) => (
                  <div key={f.bottom} className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-2">
                      <f.Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-bold text-slate-800 text-xs leading-tight">{f.top}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{f.bottom}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side image overlay */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-primary/15 to-[#21BEE1]/15 blur-3xl rounded-[48px]" />
              <img 
                src={BlogImg} 
                alt="SmartEdz Blog Updates" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CATEGORY FILTER ================= */}
      <section className={`${CONTAINER} py-8`}>
        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
          {CATEGORIES.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition duration-300 ${
                activeCategory === category 
                  ? 'bg-primary text-white shadow-md shadow-primary/20' 
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* ================= BLOG GRID ================= */}
      <section className={`${CONTAINER} pb-20`}>
        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.id} 
                className="group bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
              >
                {/* Image Container */}
                <div className="relative h-56 overflow-hidden bg-slate-100">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/800x400?text=SmartEdz+Blog'; }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/90 backdrop-blur-sm text-xs font-bold text-primary shadow-sm">
                      <Tag className="w-3 h-3" /> {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      {post.readTime}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-primary transition" style={{ color: '#13294b' }}>
                    <Link to={`/blog/${post.id}`}>{post.title}</Link>
                  </h3>
                  
                  <p className="text-sm text-slate-500 leading-relaxed mb-6 line-clamp-3 flex-grow">
                    {post.excerpt}
                  </p>

                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-400" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{post.author}</span>
                    </div>
                    
                    
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="py-20 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-slate-50 flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">No articles found</h3>
            <p className="text-slate-500">We couldn't find any posts matching your search criteria.</p>
            <button 
              onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
              className="mt-6 px-6 py-2.5 rounded-lg bg-primary/10 text-primary font-semibold hover:bg-primary/20 transition"
            >
              Clear Filters
            </button>
          </div>
        )}
      </section>

    </div>
  );
};

export default Blog;